import React, { Suspense } from 'react';
import noop from 'lodash/noop';

import Platform from '@lyearn/core/utils/Platform';
import { useTranslation } from '@lyearn/i18n';
import { BottomSheet, Stack } from '@lyearn/ui';
import { PageError } from '@/components/Error';
import Loader from '@/components/Loader';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import KeyboardAvoidance from '@/components/PageLayout/components/KeyboardAvoidance';
import useWindowSize from '@/hooks/useWindowSize';
import DoesNotExist from '@/modules/corporate/components/DoesNotExist';
import SurveyContent from '@/modules/corporate/modules/survey/views/SurveyDetails/components/SurveyContent';
import useSurveyDetails from '@/modules/corporate/modules/survey/views/SurveyDetails/hooks/useSurveyDetails';

import SurveyModalHeader from './components/SurveyModalHeader';
import { SurveyModalProps } from './types';

const isNative = Platform.OS !== 'web';

const SurveyModal: React.FC<SurveyModalProps> = (props) => {
  useTranslation(['Database', 'SurveyEditor']);
  const { parentContentId, surveyId, onClose, isEmbed } = props;
  const { height } = useWindowSize();
  const nativeStyles = React.useMemo(
    () => (isNative ? { height: height * 0.9 } : undefined),
    [height],
  );
  const { fields, fetching, error, survey, backActionText } = useSurveyDetails({
    contentId: surveyId,
    isEmbed,
    parentContentId,
  });

  const renderContent = () => {
    if (fetching) {
      return <Loader />;
    }

    if (error) {
      return <PageError error={error} />;
    }

    if (!survey) {
      return <DoesNotExist />;
    }

    return (
      <Stack direction="col" style={nativeStyles}>
        <SurveyModalHeader survey={survey} />
        <KeyboardAvoidance>
          <SurveyContent
            backActionText={backActionText}
            fields={fields}
            isEmbed={isEmbed}
            parentContentId={parentContentId}
            showHeader={!isNative}
            surveyContent={survey}
            onBack={onClose}
          />
        </KeyboardAvoidance>
      </Stack>
    );
  };

  if (isNative) {
    return (
      <BottomSheet open enableContentPanningGesture={false} onClose={onClose} onOpen={noop}>
        {renderContent()}
      </BottomSheet>
    );
  }

  return (
    <Modal variant={ModalVariant.FullScreen} onClose={onClose}>
      <Suspense fallback={<Loader />}>{renderContent()}</Suspense>
    </Modal>
  );
};

export default React.memo(SurveyModal);
